
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import UserModule from '@/store/modules/User';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import api from '@/service/api';
import Debounce from '@/decorators/debounce';

const userState = getModule(UserModule, store);

@Component
export default class Registration extends Vue {
    @Prop({ default: true }) public showFacebookAuth!: boolean;
    public termsAccepted = false;
    public regComplete = false;
    public regEmail = '';
    public regPassword = '';
    public repeatPassword = '';
    public valid = true;
    public emailRules = [
        (v: string) => !!v || 'Email jest wymagany',
        (v: string) => /^[\w.-]+@([\w-]+\.)+[\w-]+$/.test(v) || 'Email nie jest poprawny',
    ];
    public emailErrors: string[] = [];
    public registrationErrors: string[] = [];
    public passwordRules = [
        (v: string) => !!v || 'Hasło jest wymagane',
        (v: string) => /((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))/.test(v) || 'Hasło musi mieć minimum 6 znaków, dużą i małą literę oraz znak specjalny.',
    ];
    public termsAcceptedRules = [
        (v: boolean) => v || 'Zaakceptuj regulamin i politykę prywatności',
    ];
    get repeatPasswordRules() {
        return [
            (v: string) => !!v || 'Hasło jest wymagane',
            (v: string) => v === this.regPassword || 'Hasła muszą być identyczne',
        ];
    }
    public emailCheckingInProgress = false;
    public isLoadingFb = false;
    public isLoadingRegister = false;
    pendingSwitchToLogin = false;
    successModalCanClose = false;
    private readonly emailPattern = /^[\w.-]+@([\w-]+\.)+[\w-]+$/;
    private readonly passwordPattern = /((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))/;

    get canSubmitRegistration() {
        const email = this.regEmail.trim();
        const password = this.regPassword;
        return this.termsAccepted
            && !this.isLoadingFb
            && !this.isLoadingRegister
            && !this.emailCheckingInProgress
            && this.emailErrors.length === 0
            && email.length > 0
            && password.length > 0
            && this.repeatPassword.length > 0
            && this.emailPattern.test(email)
            && this.passwordPattern.test(password)
            && this.repeatPassword === password;
    }

    public async fbLogin() {
        try {
            this.isLoadingFb = true;
            await userState.authenticateFacebook();
        } catch (e) {
            if (e.response && e.response.status === 403) {
                userState.setRegistrationResolver(async (code: string) => {
                    await userState.authenticateFacebook(code);
                });
            } else {
                throw e;
            }
        } finally {
            this.isLoadingFb = false;
        }
    }

    public async register() {
        this.registrationErrors = [];

        if (!this.canSubmitRegistration) {
            (this.$refs.form as any).validate();
            if (this.emailCheckingInProgress) {
                this.registrationErrors = ['Sprawdzamy adres e-mail. Spróbuj ponownie za chwilę.'];
            }
            return;
        }

        if ((this.$refs.form as any).validate() && !this.emailCheckingInProgress) {
            this.isLoadingRegister = true;
            try {
                await api.registerUser(this.regEmail!, this.regPassword!);
                this.onRegistrationSuccess();
            } catch (e) {
                const responseStatus = (e as any)?.response?.status;
                const responseData = (e as any)?.response?.data;
                const testCodeReason = responseData?.reason;
                const isTestCodeFlow = responseStatus === 403
                    || responseData?.error === 'Test code error'
                    || testCodeReason === 'noCode'
                    || testCodeReason === 'invalidCode'
                    || (typeof responseStatus === 'number' && responseStatus !== 409);

                if (isTestCodeFlow) {
                    userState.setRegistrationResolver(async (code: string) => {
                        await api.registerUser(this.regEmail!, this.regPassword!, code);
                        this.onRegistrationSuccess();
                    });
                } else if (responseStatus === 409) {
                    this.emailErrors = ['Taki adres jest już zarejestrowany'];
                } else if ((e as any)?.response) {
                    this.registrationErrors = ['Nie udało się utworzyć konta. Spróbuj ponownie.'];
                } else {
                    throw e;
                }
            } finally {
                this.isLoadingRegister = false;
            }
        }
    }

    private onRegistrationSuccess() {
        this.pendingSwitchToLogin = true;
        this.successModalCanClose = false;
        // Open success modal first, then close tester-code modal.
        this.regComplete = true;
        window.setTimeout(() => {
            userState.setProvideCode(false);
        }, 30);
        // Prevent click bleed-through from the "Wyślij" button instantly closing success modal.
        window.setTimeout(() => {
            this.successModalCanClose = true;
        }, 260);
    }

    requestCloseSuccessModal() {
        if (!this.successModalCanClose) {
            return;
        }
        this.regComplete = false;
    }

    @Watch('regComplete')
    onRegCompleteChange(isOpen: boolean) {
        if (!isOpen && this.pendingSwitchToLogin) {
            this.pendingSwitchToLogin = false;
            this.$emit('switch-to-login');
        }
    }

    @Debounce(500)
    public async checkEmail() {
        const trimmedEmail = this.regEmail.trim();
        this.registrationErrors = [];

        try {
            if (trimmedEmail.length > 0 && await api.emailExists(trimmedEmail)) {
                this.emailErrors = ['Taki adres jest już zarejestrowany'];
            } else {
                this.emailErrors = [];
            }
        } catch (e) {
            this.emailErrors = [];
            this.registrationErrors = ['Nie udało się sprawdzić adresu e-mail. Spróbuj ponownie.'];
        } finally {
            this.emailCheckingInProgress = false;
        }
    }
}
